'use client';
import { nonPiiPost } from '@cecm-microservices-sdk/core/services/account/non-pii';
import { infoPost } from '@cecm-microservices-sdk/core/services/device/info';
import { validatePost } from '@cecm-microservices-sdk/core/services/device/validate';
import { licensePlatePost } from '@cecm-microservices-sdk/core/services/identity/device/license-plate';
import type { FlexibleFormVariant } from './FlexibleFormBase';
import { applyRflzEligibilityResult, getRequiredRflzFieldNames } from './rflzFlexibleFormSubmit';
import { handleRflzResponse } from './rflzResponseHandler';
import { submitLicensePlateEligibility } from './submitLicensePlateEligibility';
import { LICENSE_PLATE_SUBMIT_MESSAGES } from './licensePlateSubmitMessages';
import {
    getMicroservicesCountryCode,
    submitUsedCarEligibilityRequest,
} from '@/services/microservices/usedCarEligibility';
import { getMicroservicesClient } from '@/services/microservices/client';
import { setFlexibleFormSubmitError } from './flexibleFormShared';
import { findFlexibleFormField, normalizeFlexibleFormFieldKey } from './flexibleFormFieldMatching';

function getRequiredLicensePlateEligibilityFieldNames(formFields: SiteFormField[]) {
    const rflzFieldNames = getRequiredRflzFieldNames(formFields);
    const hasStateField = formFields.some((field) => {
        const normalizedType = normalizeFlexibleFormFieldKey(field.fields.type);
        return normalizedType === 'state' || normalizedType === 'stateandzip';
    });

    return {
        firstName: rflzFieldNames.firstName,
        lastName: rflzFieldNames.lastName,
        zipCode: hasStateField ? rflzFieldNames.zipCode || 'zip' : rflzFieldNames.zipCode,
    };
}

function getRequiredLicensePlateFieldNames(formFields: SiteFormField[]) {
    const licensePlateField = findFlexibleFormField(
        formFields,
        ({ type, fieldName, displayName, label }) =>
            type === 'licenseplate' ||
            type === 'licenseplatenumber' ||
            fieldName === 'licenseplate' ||
            fieldName === 'licenseplatenumber' ||
            displayName.includes('licenseplate') ||
            label.includes('licenseplate'),
    );
    const stateField = findFlexibleFormField(
        formFields,
        ({ type, fieldName, displayName, label }) =>
            type === 'state' || fieldName === 'state' || displayName === 'state' || label === 'state',
    );

    return {
        licensePlate: licensePlateField?.fields.field_name,
        state: stateField?.fields.field_name,
    };
}

export function createLicensePlateCustomSubmit(formFields: SiteFormField[]): FlexibleFormVariant['customSubmit'] {
    const licensePlateFieldNames = getRequiredLicensePlateFieldNames(formFields);
    const licensePlateEligibilityFieldNames = getRequiredLicensePlateEligibilityFieldNames(formFields);

    return async (data, context) => {
        try {
            if (!licensePlateFieldNames.licensePlate || !licensePlateFieldNames.state) {
                setFlexibleFormSubmitError(context, LICENSE_PLATE_SUBMIT_MESSAGES.missingPlateConfig);
                return;
            }

            const licensePlate = ((data.get(licensePlateFieldNames.licensePlate) as string) || '').trim();
            const state = ((data.get(licensePlateFieldNames.state) as string) || '').trim();
            const countryCode = getMicroservicesCountryCode();
            const client = getMicroservicesClient(countryCode);

            if (!client.baseUrl) {
                setFlexibleFormSubmitError(context, LICENSE_PLATE_SUBMIT_MESSAGES.missingBaseUrl(countryCode));
                return;
            }

            const firstName = ((data.get(licensePlateEligibilityFieldNames.firstName || '') as string) || '').trim();
            const lastName = ((data.get(licensePlateEligibilityFieldNames.lastName || '') as string) || '').trim();
            const zipCode = ((data.get(licensePlateEligibilityFieldNames.zipCode || '') as string) || '').trim();

            if (
                !licensePlateEligibilityFieldNames.firstName ||
                !licensePlateEligibilityFieldNames.lastName ||
                !licensePlateEligibilityFieldNames.zipCode ||
                !firstName ||
                !lastName ||
                !zipCode
            ) {
                setFlexibleFormSubmitError(context, LICENSE_PLATE_SUBMIT_MESSAGES.missingEligibilityConfig);
                return;
            }

            const result = await submitLicensePlateEligibility(
                { licensePlate, state, firstName, lastName, zipCode },
                {
                    client,
                    countryCode,
                    licensePlatePost,
                    validatePost,
                    nonPiiPost,
                    infoPost,
                    submitUsedCarEligibilityRequest,
                    handleRflzResponse,
                },
            );

            applyRflzEligibilityResult(result, context);
        } catch (error) {
            console.error('License plate submit flow failed', error);
            setFlexibleFormSubmitError(context, LICENSE_PLATE_SUBMIT_MESSAGES.systemError);
        }
    };
}
